import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    SortingState,
    PaginationState,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../../../constant/assest";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../component/common/CustomButton/CustomButton";
import cn from 'classnames';
import UserCard from "../../../../../component/common/UserCard/UserCard";
import { useMemo, useState } from "react";
import Pagination from "../../../../../component/UI/Pagination/Pagination";
import { PageRoutes } from "../../../../../routing/paths";
import { ReferralUser } from "../../../../../redux/slices/referralTracker/types";
import TableSkeleton from "../../../../../component/common/TableSkeleton";
import { usePagination } from "../../../../../hooks/usePagination";

type ReferralTrackingTableProps = {
    data: ReferralUser[];
    isLoading: boolean;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
};

const ReferralTrackingTable = ({
    data,
    isLoading,
    page,
    limit,
    total,
    totalPages,
    onPageChange,
    onLimitChange,
}: ReferralTrackingTableProps) => {
    const navigate = useNavigate();
    
    const columns = useMemo<ColumnDef<ReferralUser>[]>(() => [
        {
            header: "Inviter Name",
            accessorKey: "full_name",
            cell: (info) => {
                const row = info.row.original;
                return (
                    <UserCard
                        userName={row.full_name || 'N/A'}
                        userHandle={row.username}
                        userImage={row.profile}
                    />
                );
            },
            sortingFn: (rowA, rowB, columnId) => {
                const a = rowA.getValue(columnId) as string || "";
                const b = rowB.getValue(columnId) as string || "";
                return a.localeCompare(b);
            },
        },
        {
            header: "Email",
            accessorKey: "email",
            cell: (info) => info.getValue() || "N/A",
            sortingFn: (rowA, rowB, columnId) => {
                const a = rowA.getValue(columnId) as string || "";
                const b = rowB.getValue(columnId) as string || "";
                return a.localeCompare(b);
            },
        },
        {
            header: "Invitations Sent",
            accessorKey: "totalReferrals",
            cell: (info) => info.getValue() ?? "N/A",
        },
        {
            header: "Sign-Ups Confirmed",
            accessorKey: "successfulReferrals",
            cell: (info) => {
                const row = info.row.original;
                return (
                    <div className={'confirmedCell'}>
                        <span>{row.successfulReferrals ?? 'N/A'}</span>
                        <span className={cn('confirmedRateBadge')}>
                            {row.conversion_percentage ?? 'N/A'}
                        </span>
                    </div>
                );
            },
        },
        // { header: "Total Rewards", accessorKey: "rewards" },
        {
            header: "",
            id: "action",
            cell: ({row}) => (
                <CustomButton
                    btnType={ButtonType.IMAGE}
                    onClick={() => {
                        navigate(`${PageRoutes.PROACCOUNTS}/${PageRoutes.PORTFOLIOS}/${row.original.referrerId}/#referrals`);
                    }}
                >
                    <Image src={ASSESTS.CHEVRONRIGHT} alt="Action" width={20} height={20} />
                </CustomButton>
            ),
            enableSorting: false,
        },
    ], [navigate]);

    const [sorting, setSorting] = useState<SortingState>([]);

    const pagination = useMemo<PaginationState>(() => ({
        pageIndex: page - 1,
        pageSize: limit,
    }), [page, limit]);

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination },
        onSortingChange: setSorting,
        sortDescFirst: false,
        pageCount: totalPages,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });
    const paginationGroup = usePagination({
            currentPageIndex: pagination.pageIndex,
            totalPages: totalPages,
          });
        
          const pageNumbersElements = paginationGroup.map((page, index) => {
            if (typeof page === 'string') {
              return <span key={`ellipsis-${index}`} className='pageNumberBtn' style={{cursor: 'default'}}>{page}</span>;
            }
        
            const pageIndex = page;
            return (
              <CustomButton
                key={pageIndex}
                onClick={() => onPageChange(pageIndex + 1)}
                disabled={pagination.pageIndex === pageIndex}
                btnType={
                  pagination.pageIndex === pageIndex
                    ? ButtonType.ACTIVENOSTYLE
                    : ButtonType.NOSTYLE
                }
                btnSize={ButtonSize.NORMAL}
                btnFont={
                  pagination.pageIndex === pageIndex
                    ? ButtonFont.BOLD
                    : ButtonFont.NORMAL
                }
                className={cn(
                  'pageNumberBtn',
                  pagination.pageIndex === pageIndex && 'activePageNumberBtn'
                )}
              >
                {pageIndex + 1}
              </CustomButton>
            );
          });

    return (
        <div className='tableContainer'>
            <div className='tableWrapper'>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isActionColumn = typeof header.column.columnDef.header === "string" && header.column.columnDef.header === "Action";
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                cursor: header.column.getCanSort() ? "pointer" : "default"
                                            }}
                                            className={cn(header.column.columnDef.id && `th-${header.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getCanSort() &&
                                                <span className={cn('tableShorting')}>
                                                    <Image
                                                        src={header.column.getIsSorted() === "asc" ? ASSESTS.ASSENDING : header.column.getIsSorted() === "desc" ? ASSESTS.DECENDING : ASSESTS.SHORT}
                                                        alt="Short"
                                                    />
                                                </span>
                                            }
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableSkeleton rows={limit} columns={columns.length} />
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className='noResultsFound'>
                                    No results found
                                </td>
                            </tr>
                        ) :
                         table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    const isActionColumn = typeof cell.column.columnDef.header === "string" && cell.column.columnDef.header === "Action";

                                    return (
                                        <td
                                            key={cell.id}
                                            className={cn(cell.column.columnDef.id && `td-${cell.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination
                enteriesShows={`${total
                        ? pagination.pageIndex * pagination.pageSize + 1
                        : 0
                    }-${Math.min(
                        (pagination.pageIndex + 1) * pagination.pageSize,
                        total || 0
                    )} of ${total || 0} Entries`}

                pageNumbers= {[
                    totalPages > 1 && (
                        <CustomButton
                            key="prev"
                            onClick={() => onPageChange(page - 1)}
                            disabled={!table.getCanPreviousPage()}
                            btnType={ButtonType.NOSTYLE}
                            className='pageNumberBtn'
                        >
                            Prev
                        </CustomButton>
                    ),
                    ...pageNumbersElements,
                    totalPages > 1 && (
                        <CustomButton
                            key="next"
                            onClick={() => onPageChange(page + 1)}
                            disabled={!table.getCanNextPage()}
                            btnType={ButtonType.NOSTYLE}
                            className='pageNumberBtn'
                        >
                            Next
                        </CustomButton>
                    )
                    ].filter(Boolean)}
                dropDownOptions={[
                    { value: "5", label: "5" },
                    { value: "10", label: "10" },
                    { value: "20", label: "20" },
                    { value: "25", label: "25" },
                ]}
                dropdownOnChange={(value) => {
                    onLimitChange(Number(value));
                    onPageChange(1);
                }}
                dropDownPlaceholder={pagination.pageSize.toString()}
            />
        </div>
    );
};

export default ReferralTrackingTable;

